import { type JewelSocketProfile, REGENERATION_JEWEL_ID, toExactBigInt } from '@kenocyber/shared';
import { BPS_ONE } from './exact';
import { HEAL_MILLI_PER_UNIT } from './lifesteal';
import type { DamageOrigin } from './origin';
import { damageOriginMayTrigger } from './origin';
import { PROC_CHANCE_DENOMINATOR, PROC_LEVEL_CAP, procChanceNumerator } from './proc-lottery';

/**
 * **O Coração Vital: uma rolagem por SALVA, e uma cura de SUPORTE.**
 *
 * ## Porque um ficheiro próprio, e porque ele não entra em loteria nenhuma
 *
 * Porque esta é a primeira joia que sorteia do lado de quem AJUDA. As duas
 * loterias que já existem repartem uma pergunta cada uma:
 *
 * - `proc-lottery.ts` - "o que este PROJÉTIL produz", com intervalos disjuntos
 *   sobre uma rolagem, porque duas coisas não podem ocupar o mesmo tiro;
 * - `volley-lottery.ts` - "o que esta SALVA produz", pela mesma razão, na
 *   unidade em que a Bomba e o Míssil vivem.
 *
 * O Coração Vital responde a uma terceira: **"esta salva também curou alguém?"**
 * A resposta não compete com nenhuma das outras duas, porque uma cura não ocupa
 * um projétil nem substitui um. O contrato do gate diz-o por escrito - *«é um
 * proc de suporte independente: não consome nem bloqueia a loteria ofensiva»* -
 * e um intervalo disjunto entre "curou" e "lançou um míssil" não significaria
 * nada: são dois acontecimentos que PODEM ser simultâneos, e uma salva que
 * legitimamente lança um míssil e cura um aliado é uma salva legal.
 *
 * Duas coisas que podem acontecer ao mesmo tempo precisam de dois sorteios. É a
 * mesma razão pela qual `poison.ts` e `bleeding.ts` também não estão numa
 * loteria - e a diferença é que aqueles sorteiam por ACERTO, e este por SALVA.
 *
 * ## Um fluxo de números próprio, e a razão é a de sempre
 *
 * Consumir a rolagem do fluxo por salva - `hunt-jewel-volley-bomb-v1` - mudaria
 * toda a sequência da Bomba e do Míssil a partir do primeiro disparo, e uma
 * corrida com a mesma semente deixaria de ser a mesma corrida por causa de uma
 * joia que o jogador nem tem. O fluxo é separado, rotulado e consumido SEMPRE,
 * com joia ou sem ela.
 *
 * O §"não pode deslocar RNG de runas, ofensivos, Bomba, Míssil, Veneno,
 * Sangramento ou qualquer outro sistema" é cumprido por construção: nada neste
 * ficheiro toca noutro gerador, e o rótulo é novo.
 *
 * ## O que este ficheiro NÃO faz
 *
 * Não sorteia - recebe a rolagem já feita, como todos os outros registos, porque
 * quem a faz é a simulação com o seu fluxo autoritativo. Não conhece uma cena,
 * um sprite, uma cor nem uma duração. Não conhece um mob, um boss nem um
 * projétil. E não sabe o que é um aliado: recebe candidatos já medidos, e devolve
 * qual deles é o escolhido.
 */

/**
 * O id da joia, nomeado uma vez.
 *
 * A joia da CURA DE SUPORTE. Como o Núcleo Atômico, o Arsenal Ígneo, o Orbe da
 * Víbora, a Garra Hemorrágica, a Égide Espinhosa e o Diamante Serrilhado, ela
 * não declara nada em `JewelEffects`: uma chance não é um atributo somado, e a
 * dela vive aqui, ao lado da magnitude e da lei de seleção de alvo.
 */
export const VITAL_HEART_JEWEL_ID = REGENERATION_JEWEL_ID;

/**
 * O teto de chance do Coração Vital: **6 %**, nomeado uma vez.
 *
 * A tabela obrigatória do gate sai desta constante e de {@link PROC_LEVEL_CAP},
 * sem uma linha escrita à mão:
 *
 * | nível | numerador | chance por salva |
 * | ---: | ---: | ---: |
 * | 0 | 0 | 0 % |
 * | 1 | 600 | 0,2 % |
 * | 10 | 6 000 | 2 % |
 * | 20 | 12 000 | 4 % |
 * | 30 | 18 000 | 6 % |
 * | 31+ | 18 000 | 6 % |
 *
 * Seis por cento é o TETO ABSOLUTO, e o congelamento acima do nível 30 é o mesmo
 * `min` que todas as outras chances do projeto usam - não um caso especial.
 */
export const VITAL_HEART_MAX_CHANCE_BPS = 600;

/**
 * **A magnitude: 150 % do dano-base autoritativo da nave.**
 *
 * ## Porque 150 %, e porque ela NÃO cresce com o nível
 *
 * Porque o poder esperado é `chance x magnitude`, e é a CHANCE que carrega o
 * nível:
 *
 *     nível 30 - 6 %   x 150 % = 9 %   do dano-base por salva, em cura
 *     nível 1  - 0,2 % x 150 % = 0,3 %
 *
 * Uma magnitude que também crescesse daria `chance(L) x magnitude(L)`, ou seja
 * uma curva QUADRÁTICA - o nível 30 valeria trinta vezes o nível 1 em chance e
 * mais trinta em tamanho, novecentas vezes ao todo. O gate proíbe-o por escrito,
 * e a maneira de não o produzir é esta constante não ter parâmetro nenhum.
 *
 * ## O que a base é, e o que ela nunca inclui
 *
 * O dano-base AUTORITATIVO e mais nada: sem a Chama da Destruição nem qualquer
 * outro aumento percentual, sem crítico, sem Ciclone, sem Serra, sem Veneno, sem
 * Sangramento, sem Míssil, sem Bomba, sem dano refletido, sem o dano
 * efetivamente aplicado e sem overkill.
 *
 * Isso não é uma regra escrita aqui - é o ARGUMENTO que o chamador passa, e as
 * duas simulações passam exatamente o mesmo `baseDamageMilli()` /
 * `pvpShipBaseDamageMilli()` que a loteria ofensiva já recebe. Um chamador que
 * passasse o dano final do projétil estaria a mentir a esta função, e é por isso
 * que o teste da magnitude compara contra a base e não contra o tiro.
 */
export const VITAL_HEART_HEAL_SHARE_BPS = 15_000n;

/**
 * Os rótulos dos fluxos, estáveis e VERSIONADOS.
 *
 * Versionados porque mudar quantos valores uma salva consome muda toda a
 * sequência a partir daí. Um rótulo novo é a forma de declarar essa mudança em
 * vez de a deixar acontecer em silêncio numa sala já a decorrer.
 */
export const PVP_VITAL_HEART_RNG_LABEL = 'pvp-jewel-vital-heart-v1';
export const HUNT_VITAL_HEART_RNG_LABEL = 'hunt-jewel-vital-heart-v1';
/**
 * O gémeo do MUNDO ABERTO, com a mesma finalidade e a mesma unidade.
 *
 * Um rótulo próprio, e não uma reutilização: um rótulo é o que separa duas
 * sequências, e "de que modo é este fluxo" tem de continuar a ser legível numa
 * linha de import. A cadência e a razão de este fluxo ser separado dos outros
 * são as mesmas de sempre.
 */
export const OPEN_WORLD_VITAL_HEART_RNG_LABEL = 'open-world-jewel-vital-heart-v1';

/**
 * O numerador desta nave, dado o seu nível somado.
 *
 * Inteiro, e nunca dividido - `13/30` não tem representação decimal finita, e
 * dividir antes de comparar transformaria 0,26 % em 0,26 % arredondado. A
 * rolagem vive em `[0, PROC_CHANCE_DENOMINATOR)` e é comparada com isto.
 */
export function vitalHeartChanceNumerator(levelSum: number): number {
  return procChanceNumerator(VITAL_HEART_MAX_CHANCE_BPS, levelSum);
}

/**
 * A cura que uma ativação PEDE, em milli, exata.
 *
 *     pedido = base x 15 000 / 10 000
 *
 * Uma única divisão, no fim, e em `bigint` até lá. `base` não tem tecto de jogo -
 * a contagem de Corações Primordiais vive em `numeric(40, 0)` - portanto nada
 * aqui pode ser um `number`, nem sequer para imprimir.
 *
 * Não recebe o nível, e é essa ausência que garante o §"magnitude não cresce
 * novamente com o nível". Não há por onde a fazer crescer.
 */
export function vitalHeartHealMilli(baseMilli: bigint): bigint {
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a vital heal base is a non-negative bigint, got ${String(baseMilli)}`);
  }
  return (baseMilli * VITAL_HEART_HEAL_SHARE_BPS) / BPS_ONE;
}

/**
 * O que uma ativação produziu: a cura PEDIDA, congelada no instante do disparo.
 *
 * A magnitude é resolvida aqui e nunca reavaliada, pela mesma fronteira temporal
 * de `JewelProcSelection.cutMilli`: comprar um ponto de dano entre a ativação e
 * a aplicação não pode mudar o que esta salva curou.
 */
export interface VitalHealSelection {
  /** A cura pedida, em milli. Ainda sem alvo e sem limite nenhum. */
  readonly requestedMilli: bigint;
}

/**
 * **A seleção.** Uma rolagem por salva, e a chance é a do perfil.
 *
 * `roll` tem de vir de `[0, PROC_CHANCE_DENOMINATOR)` e ser produzido pelo fluxo
 * autoritativo DESTE participante. O intervalo é `[0, numerador)` - um intervalo
 * só, porque este sorteio não reparte espaço com ninguém.
 *
 * Devolve `null` quando a rolagem cai fora, quando a nave não tem a joia, quando
 * o nível somado é zero, e quando a origem não pode originar esta cura. As
 * quatro razões são diferentes e nenhuma produz cura.
 *
 * ## A tabela fechada, e o que ela recusa aqui
 *
 * `damageOriginMayTrigger(origin, 'vital_heal')` é `true` só para `cannon`. Uma
 * serra, um pet, um veneno, um sangramento, um ciclone, um míssil, uma bomba ou
 * um dano refletido não podem acionar isto - e a garantia é estrutural em vez de
 * ser uma condição que alguém tem de se lembrar de escrever: não há chamador
 * fora do caminho da salva, e se alguém escrevesse um, esta linha recusava-o.
 */
export function selectVitalHeal(
  profile: JewelSocketProfile,
  origin: DamageOrigin,
  roll: number,
  baseMilli: bigint,
): VitalHealSelection | null {
  if (!damageOriginMayTrigger(origin, 'vital_heal')) {
    return null;
  }
  if (!Number.isInteger(roll) || roll < 0 || roll >= PROC_CHANCE_DENOMINATOR) {
    throw new RangeError(`vital heal roll outside [0, ${PROC_CHANCE_DENOMINATOR}): ${roll}`);
  }
  if (typeof baseMilli !== 'bigint' || baseMilli < 0n) {
    throw new RangeError(`a vital heal base is a non-negative bigint, got ${String(baseMilli)}`);
  }
  const owned = profile.entries.find((entry) => entry.id === VITAL_HEART_JEWEL_ID);
  if (owned === undefined) {
    return null;
  }
  const levelSum = toExactBigInt(owned.levelSum, `${owned.id} levelSum`);
  const width = vitalHeartChanceNumerator(Number(levelSum));
  if (width <= 0 || roll >= width) {
    return null;
  }
  return Object.freeze({ requestedMilli: vitalHeartHealMilli(baseMilli) });
}

/* -------------------------------------------------------------------------- */
/*  A SELEÇÃO DE ALVO                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Um candidato a receber a cura, já MEDIDO por quem o conhece.
 *
 * Este ficheiro não sabe o que é uma equipa, uma cadeira, um casco ou uma
 * unidade de arena: recebe uma identidade estável, uma ordem canónica, uma
 * distância ao quadrado e se o casco precisa mesmo de cura. É a mesma disciplina
 * que faz `lifesteal.ts` não conhecer joia nenhuma.
 */
export interface VitalHealCandidate {
  /** A identidade AUTORITATIVA e estável do participante. Nunca uma posição. */
  readonly id: string;
  /**
   * A ordem canónica desta identidade. **O único desempate permitido.**
   *
   * Nunca o índice num array: a ordem de um array é incidental - depende de como
   * uma sala foi preenchida ou de por que caminho uma lista foi construída - e o
   * gate exige um desempate "por identidade estável do participante". No duelo
   * isto é `PvpShipRoster.indexOf`, que é uma propriedade da cadeira.
   */
  readonly order: number;
  /**
   * A distância AO QUADRADO ao curador, na unidade inteira do chamador.
   *
   * Ao quadrado porque a raiz não muda nenhuma ordenação e introduziria um
   * `Math.sqrt` cujo último bit não é obrigatoriamente igual entre motores. No
   * duelo isto é milli-unidades - a arena mede 4 800 000 mu, e o quadrado da
   * diagonal cabe folgadamente num inteiro seguro.
   */
  readonly distanceSq: number;
  /** Vivo E abaixo da vida máxima. As duas metades, decididas por quem sabe. */
  readonly injured: boolean;
}

/**
 * **Quem recebe a cura.** Uma única decisão autoritativa, no instante da
 * ativação.
 *
 * A lei do gate, por ordem:
 *
 * 1. só aliados VIVOS e com vida abaixo da máxima são considerados;
 * 2. a própria nave está EXCLUÍDA desta primeira passagem;
 * 3. ganha o aliado ferido mais PRÓXIMO;
 * 4. um empate é desfeito pela menor {@link VitalHealCandidate.order}, nunca
 *    pela ordem incidental do array;
 * 5. sem aliado ferido, cura-se a própria nave - se estiver viva e ferida;
 * 6. se ninguém precisar, devolve `null`: a rolagem foi consumida na mesma, e não
 *    há cura nem evento.
 *
 * Não existe troca de alvo entre o disparo e o evento: quem chama isto aplica a
 * cura no mesmo instante, e a identidade devolvida é a que o evento carrega.
 */
export function selectVitalHealTarget(
  allies: readonly VitalHealCandidate[],
  self: VitalHealCandidate,
): VitalHealCandidate | null {
  let best: VitalHealCandidate | null = null;
  for (const ally of allies) {
    if (ally.id === self.id) {
      /*
       * A própria nave nunca compete com os aliados.
       *
       * Uma guarda e não uma responsabilidade do chamador: uma lista de "a minha
       * equipa" inclui-me por definição em qualquer roster, e esquecê-la faria a
       * autocura ganhar sempre - a distância a mim próprio é zero.
       */
      continue;
    }
    if (!ally.injured) {
      continue;
    }
    if (
      best === null ||
      ally.distanceSq < best.distanceSq ||
      (ally.distanceSq === best.distanceSq && ally.order < best.order)
    ) {
      best = ally;
    }
  }
  if (best !== null) {
    return best;
  }
  return self.injured ? self : null;
}

/* -------------------------------------------------------------------------- */
/*  AS DUAS FRONTEIRAS DA BARRA                                               */
/* -------------------------------------------------------------------------- */

/**
 * O que a cura fez a uma barra: **o que ela REALMENTE recebeu**, em milli.
 *
 * Nunca o pedido. O evento de apresentação carrega este número, e é ele que o
 * `+TOTAL` soma - `appliedMilli` zero quer dizer que não aconteceu nada e que
 * não há nada a desenhar.
 */
export interface VitalHealApplication {
  /** A cura PEDIDA, para quem quiser saber quanto a joia valia. */
  readonly requestedMilli: bigint;
  /** O que a barra aceitou, em milli. Zero num casco cheio ou em baixo. */
  readonly appliedMilli: bigint;
}

const NOTHING: VitalHealApplication = Object.freeze({ requestedMilli: 0n, appliedMilli: 0n });

/**
 * **A fronteira para uma barra em MILLI.** O duelo.
 *
 * Sem overheal, sem escudo, sem ressurreição: um casco em baixo recebe zero, um
 * casco cheio recebe zero, e o excedente é DESCARTADO em vez de guardado -
 * guardá-lo faria uma estadia com a vida cheia virar um salto no primeiro
 * instante depois de levar dano.
 *
 * Não há resto sub-milli para segurar, ao contrário do Roubo de Vida: a cura já
 * nasce em milli inteiro, porque `vitalHeartHealMilli` divide uma vez e no fim.
 */
export function applyVitalHealMilli(input: {
  readonly requestedMilli: bigint;
  readonly healthMilli: bigint;
  readonly maxHealthMilli: bigint;
}): VitalHealApplication {
  const { requestedMilli, healthMilli, maxHealthMilli } = input;
  if (requestedMilli <= 0n || healthMilli <= 0n) {
    /* Um destroço não recebe cura, e não ressuscita. */
    return NOTHING;
  }
  const missing = maxHealthMilli - healthMilli;
  if (missing <= 0n) {
    return Object.freeze({ requestedMilli, appliedMilli: 0n });
  }
  return Object.freeze({
    requestedMilli,
    appliedMilli: requestedMilli < missing ? requestedMilli : missing,
  });
}

/**
 * **A fronteira para uma barra FRACIONÁRIA.** A caça.
 *
 * A vida de uma nave de caça é um `number` - `maxHealth()` é uma composição
 * inteira dividida uma vez - portanto ela absorve a fração de uma cura
 * diretamente, e não há segundo resto a guardar.
 *
 * ## O limite é uma comparação, nunca uma conversão
 *
 * A vida em falta é escalada PARA CIMA em milli, dando um tecto exato que um
 * `double` sabe sempre exprimir. Uma cura maior do que ele entrega exatamente a
 * vida em falta e **nada é convertido**; uma cura menor está provada pequena
 * antes de o ser. É a mesma disciplina de `resolveLifestealHeal`, e existe pela
 * mesma razão: um casco com Corações incorporados produz curas de dezenas de
 * dígitos, e converter antes de comparar seria capar um item num tipo.
 */
export function applyVitalHealFractional(input: {
  readonly requestedMilli: bigint;
  readonly currentHealth: number;
  readonly maxHealth: number;
}): VitalHealApplication {
  const { requestedMilli, currentHealth, maxHealth } = input;
  if (!Number.isFinite(currentHealth) || !Number.isFinite(maxHealth)) {
    throw new RangeError(`health must be finite, got ${currentHealth}/${maxHealth}`);
  }
  if (requestedMilli <= 0n || currentHealth <= 0) {
    return NOTHING;
  }
  const missing = maxHealth - currentHealth;
  if (missing <= 0) {
    return Object.freeze({ requestedMilli, appliedMilli: 0n });
  }
  const ceilingMilli = BigInt(Math.ceil(missing * HEAL_MILLI_PER_UNIT));
  return Object.freeze({
    requestedMilli,
    appliedMilli: requestedMilli < ceilingMilli ? requestedMilli : ceilingMilli,
  });
}

/**
 * A cura aplicada, na unidade fracionária que a caça soma à sua barra.
 *
 * Separada de {@link applyVitalHealFractional} porque a conversão só é legítima
 * DEPOIS de o clamp a ter provado pequena: `appliedMilli` está sempre em
 * `[0, ceil(vidaEmFalta x 1000)]`, e essa cota é um inteiro seguro por
 * construção. Converter antes seria o defeito que a fronteira existe para
 * impedir.
 */
export function vitalHealPoints(appliedMilli: bigint): number {
  return Number(appliedMilli) / HEAL_MILLI_PER_UNIT;
}
